import React, {useContext, useMemo, useState} from 'react';
import {observer} from 'mobx-react';
import {SkeletonText} from '@carbon/react';
import {NetworkContext} from '../../../lib/context';
import {
    formatRosterLabel,
    KIND_SEPARATOR,
    REVISION_SEPARATOR,
    rosterKey,
    rosterLabelParts,
} from '../../../lib/roster-label';
import {ProviderRostersStatus} from '../../../lib/stores/roster-store';
import RosterBadge, {RosterSlot} from '../../../../../components/roster-badge';
import {Information, Warning} from '@carbon/icons-react';
import {useProviderRosterList} from '../../hooks/use-provider-roster-list';
import {useRosterListRetry} from '../../hooks/use-roster-list-retry';
import {RemoveButtonProps, useRemovableChips} from '../../hooks/use-removable-chips';
import {useScreenReaderAnnouncement} from '../../comparison-empty-state/useScreenReaderAnnouncement';
import RosterListError from '../../roster-list-error';
import NOFilter from '../no-filter';
import './roster-comparison-multiselect.scss';

// Fields this component reads from the store's provider roster records
type ProviderRoster = {
    rosterId: string;
    revisionId: string;
    revisionName: string;
    rosterName: string;
};

// Groups rosters by rosterId, revisions in order, with firstRosterId's group (the primary's
// other revisions) first so they're easy to find in a long list
function groupAndSortByRosterId(data: ProviderRoster[], firstRosterId?: string): ProviderRoster[] {
    // Group by rosterId
    const grouped = data.reduce<Record<string, ProviderRoster[]>>((acc, obj) => {
        if (!acc[obj.rosterId]) acc[obj.rosterId] = [];
        acc[obj.rosterId].push(obj);
        return acc;
    }, {});

    const isFirst = (group: ProviderRoster[]) => Number(group[0].rosterId === firstRosterId);

    // Roster IDs are numeric, so Object.values lists the groups in ID order; move the first
    // group to the top, then flatten and sort by revisionId within each group
    return Object.values(grouped)
        .sort((a, b) => isFirst(b) - isFirst(a))
        .flatMap((group) =>
            group.sort((a, b) => Number(a.revisionId) - Number(b.revisionId)),
        );
}

// Names the chip group as well as labelling the field
const LABEL_ID = 'comparison-rosters-label';

// Dropdown accessors: the roster's key as its value, and its label as the option content.
// Module-level so Select's memoised search data is not rebuilt on every render
const ROSTER_OPTION_ACCESSORS: [(item: ProviderRoster) => string, (item: ProviderRoster) => React.ReactNode] = [
    (item) => rosterKey(item.rosterId, item.revisionId),
    formatRosterLabel,
];

type RosterChipProps = {
    slot: RosterSlot;
    roster: ProviderRoster;
    isPrimary?: boolean;
    // From useRemovableChips; unused on the primary, which has no remove button
    removeButton?: RemoveButtonProps;
};

/**
 * A selected roster: slot badge, label, and either a Primary tag or a remove button.
 * The label is shortened to 'O | Roster' or 'R | Roster - Revision', with the roster and
 * revision names truncating separately so a long roster name never hides the revision
 */
const RosterChip = ({slot, roster, isPrimary = false, removeButton}: RosterChipProps) => {
    const label = formatRosterLabel(roster);
    const {kind, rosterName, revisionName} = rosterLabelParts(roster);

    return (
        <div
            className={`roster-comparison-multiselect__chip${
                isPrimary ? ' roster-comparison-multiselect__chip--primary' : ''
            }`}
        >
            <RosterBadge slot={slot} />
            {/* The short label is for sight; screen readers get the full one beside it */}
            <span className='roster-comparison-multiselect__chip-name' title={label} aria-hidden='true'>
                <span className='roster-comparison-multiselect__chip-fixed'>{`${kind[0]}${KIND_SEPARATOR}`}</span>
                <span className='roster-comparison-multiselect__chip-part'>{rosterName}</span>
                {revisionName && (
                    <>
                        <span className='roster-comparison-multiselect__chip-fixed'>{REVISION_SEPARATOR}</span>
                        <span className='roster-comparison-multiselect__chip-part'>{revisionName}</span>
                    </>
                )}
            </span>
            <span className='sr-only'>{label}</span>
            {isPrimary ? (
                <span className='roster-comparison-multiselect__chip-badge'>Primary</span>
            ) : (
                <button
                    {...removeButton}
                    className='roster-comparison-multiselect__chip-remove'
                    aria-label={`Remove roster ${label}`}
                    type='button'
                />
            )}
        </div>
    );
};

type RosterListMessageProps = {
    status: Exclude<ProviderRostersStatus, 'ready'>;
    onRetry: () => void;
};

/**
 * Shown in place of the chips and search while the roster list is loading,
 * came back empty, or failed to load
 */
const RosterListMessage = ({status, onRetry}: RosterListMessageProps) => {
    if (status === 'empty') {
        return (
            <p className='roster-comparison-multiselect__note' data-testid='comparison-rosters-empty'>
                <Warning size={16} className='roster-comparison-multiselect__note-icon' />
                <span>
                    No rosters have been uploaded for this client yet. Add one in <strong>Roster</strong>{' '}
                    before comparing.
                </span>
            </p>
        );
    }

    if (status === 'error') {
        // The one announced copy of the failure: the summary panel shows its own without announcing it
        return (
            <RosterListError
                body={'The roster list didn’t load. Nothing in your comparison has changed.'}
                onRetry={onRetry}
                className='roster-comparison-multiselect__error'
                data-testid='comparison-rosters-error'
            />
        );
    }

    // Loading, or idle with a request about to start. aria-hidden: a skeleton has nothing to announce
    return (
        <div className='roster-comparison-multiselect__skeleton' aria-hidden='true' data-testid='comparison-rosters-loading'>
            <SkeletonText width='70%' />
            <SkeletonText width='85%' />
            <SkeletonText width='55%' />
        </div>
    );
};

/**
 * Multi-select roster comparison component with badge UI
 * Displays selected rosters as chips with colored badges (1-5)
 * Supports 1 primary + up to 4 comparison rosters (5 total)
 */
const RosterComparisonMultiselect = function () {
    const networkOptimizationStore = useContext(NetworkContext);
    const comparisonStore = networkOptimizationStore.rosterComparisonStore;
    const {primaryRoster, rostersBySlot, maxTotalRosters} = comparisonStore;

    // Loads the roster list if nothing has; a failure is recorded in the store and renders as the error state
    const {status: rosterListStatus, reload: loadRosters} = useProviderRosterList();
    const {retry: retryRosters, focusRef: labelRef} = useRosterListRetry<HTMLSpanElement>(rosterListStatus, loadRosters);

    // Says what an add or remove did, as the chips change silently
    const [announcement, setAnnouncement] = useState('');
    useScreenReaderAnnouncement(announcement);

    // Local state for the dropdown (which roster is being selected)
    const [dropdownValue, setDropdownValue] = useState('');

    // The page makes the roster picked in My Files the primary; the Primary Roster select
    // replaces it with another of that roster's revisions

    // Create a roster lookup map for efficient access (avoids repeated Array.find calls)
    const rosterDetailsMap = useMemo(() => {
        const map = new Map<string, ProviderRoster>();
        networkOptimizationStore.providerRosters.forEach((r) => {
            map.set(rosterKey(r.rosterId, r.revisionId), r);
        });
        return map;
    }, [networkOptimizationStore.providerRosters]);

    // A roster missing from the list still gets a chip, named by its ID
    const getRoster = (rosterId: string, revisionId: string): ProviderRoster => {
        const details = rosterDetailsMap.get(rosterKey(rosterId, revisionId));
        return {
            rosterId,
            revisionId,
            rosterName: details?.rosterName || `Roster ${rosterId}`,
            revisionName: details?.revisionName || '',
        };
    };

    // Available rosters for comparison: every roster and revision not already selected (as the
    // primary or a comparison roster), matched on roster and revision together, so the
    // primary's other revisions stay on offer
    const availableRosters = useMemo(() => {
        const selectedKeys = new Set(rostersBySlot.map((r) => rosterKey(r.rosterId, r.revisionId)));
        const unselected = (networkOptimizationStore.providerRosters || []).filter(
            (r) => !selectedKeys.has(rosterKey(r.rosterId, r.revisionId)),
        );

        return groupAndSortByRosterId(unselected, primaryRoster?.rosterId);
    }, [networkOptimizationStore.providerRosters, rostersBySlot, primaryRoster]);

    // Handle adding a roster from the dropdown
    const handleAddRoster = (value: string) => {
        if (!value) {
            setDropdownValue('');
            return;
        }

        // Resolve the item by key rather than splitting, so IDs containing '-' are safe
        const selected = availableRosters.find((r) => rosterKey(r.rosterId, r.revisionId) === value);
        if (!selected) {
            return;
        }

        const success = comparisonStore.addRoster(selected.rosterId, selected.revisionId);

        if (success) {
            // Clear dropdown after successful add
            setDropdownValue('');
            const slot = comparisonStore.getRosterSlot(selected.rosterId, selected.revisionId);
            setAnnouncement(
                `${formatRosterLabel(selected)} added as roster ${slot}. ` +
                    `${comparisonStore.rostersBySlot.length} of ${maxTotalRosters} selected.` +
                    (comparisonStore.primaryRoster ? '' : ' No primary roster is selected yet.'),
            );
        }
    };

    // Handle removing a comparison roster (the primary can't be removed)
    const handleRemoveRoster = (key: string): boolean => {
        const roster = rostersBySlot.find((r) => rosterKey(r.rosterId, r.revisionId) === key);
        if (!roster) {
            return false;
        }
        const label = formatRosterLabel(getRoster(roster.rosterId, roster.revisionId));
        if (!comparisonStore.removeRoster(comparisonStore.getRosterIndex(roster.rosterId, roster.revisionId))) {
            return false;
        }
        const count = comparisonStore.rostersBySlot.length;
        setAnnouncement(count === 0
            ? `${label} removed. No rosters selected.`
            : `${label} removed. ${count} of ${maxTotalRosters} selected.`);
        return true;
    };

    const isPrimary = (rosterId: string, revisionId: string) =>
        primaryRoster?.rosterId === rosterId && primaryRoster.revisionId === revisionId;

    // The remove buttons, in slot order; once none is left, focus goes to the field label
    const removeButtonProps = useRemovableChips(
        rostersBySlot
            .filter((r) => !isPrimary(r.rosterId, r.revisionId))
            .map((r) => rosterKey(r.rosterId, r.revisionId)),
        handleRemoveRoster,
        labelRef,
    );

    const selectedCount = rostersBySlot.length;

    // Check if we're ready to load data
    const readyToLoadData = useMemo(() => {
        return Boolean(
            comparisonStore.comparisonFilters.standard_type &&
            comparisonStore.comparisonFilters.standard_version &&
            networkOptimizationStore.runId,
        );
    }, [
        comparisonStore.comparisonFilters.standard_type,
        comparisonStore.comparisonFilters.standard_version,
        networkOptimizationStore.runId,
    ]);

    return (
        <>
            <div className='roster-comparison-multiselect'>
                {/* Header with count */}
                <div className='roster-comparison-multiselect__header'>
                    {/* Focusable only from script: it takes focus on retry, and once the last
                        removable roster is removed */}
                    <span
                        id={LABEL_ID}
                        ref={labelRef}
                        tabIndex={-1}
                        className='roster-comparison-multiselect__label'
                    >
                        Comparable Rosters
                    </span>
                    {rosterListStatus === 'ready' && (
                        <span className='roster-comparison-multiselect__count'>
                            {`${selectedCount} of ${maxTotalRosters} selected`}
                        </span>
                    )}
                </div>

                {/* Until the roster list is ready there is nothing to select, so a message replaces the field */}
                {rosterListStatus !== 'ready' ? (
                    <RosterListMessage status={rosterListStatus} onRetry={retryRosters} />
                ) : (
                    <>
                        {/* Selected rosters as chips with slot badges; a prompt rather than a blank area when none */}
                        {selectedCount === 0 ? (
                            <p className='roster-comparison-multiselect__empty' data-testid='comparison-rosters-none-selected'>
                                No rosters selected yet &mdash; search below to add one.
                            </p>
                        ) : (
                            // One tab stop: arrows move between rosters, Delete removes the focused one
                            <div className='roster-comparison-multiselect__chips' role='group' aria-labelledby={LABEL_ID}>
                                {/* Chips stay in slot order: removing one renumbers the rest so badges
                                    run 1..N; a replaced primary's chip goes, like a removed one */}
                                {rostersBySlot.map((roster) => {
                                    const key = rosterKey(roster.rosterId, roster.revisionId);
                                    const primary = isPrimary(roster.rosterId, roster.revisionId);
                                    return (
                                        <RosterChip
                                            key={key}
                                            slot={roster.slot}
                                            roster={getRoster(roster.rosterId, roster.revisionId)}
                                            isPrimary={primary}
                                            removeButton={primary ? undefined : removeButtonProps(key)}
                                        />
                                    );
                                })}
                            </div>
                        )}

                        {/* Max rosters info text */}
                        {!comparisonStore.canAddRoster && (
                            <div className='roster-comparison-multiselect__info-text'>
                                <Information size={16} className='roster-comparison-multiselect__info-icon' />
                                <span>{`Maximum of ${maxTotalRosters} rosters. Remove one to add another.`}</span>
                            </div>
                        )}

                        {/* Dropdown to add more rosters */}
                        <div className='roster-comparison-multiselect__dropdown'>
                            <NOFilter
                                // Names the search field for assistive tech; shown visually by the header above
                                label='Comparable Rosters'
                                searchable
                                placeholder='Search Rosters'
                                spinner={networkOptimizationStore.loadingProviderRosters}
                                data-testid='comparison-roster-multiselect-dropdown'
                                data={availableRosters}
                                value={dropdownValue}
                                onChange={handleAddRoster}
                                // Rosters can only be added once the adequacy standard filters are set
                                disabled={!comparisonStore.canAddRoster || !readyToLoadData}
                                itemToString={formatRosterLabel}
                            >
                                {ROSTER_OPTION_ACCESSORS}
                            </NOFilter>
                        </div>
                    </>
                )}
            </div>
            <hr className='roster-comparison-multiselect__divider' />
        </>
    );
};

export default observer(RosterComparisonMultiselect);
